import { api } from '../api';

// ApiResponse envelope → inner payload.
const unwrap = (r) => r?.data?.data ?? r?.data;

/**
 * A turn parks the request while the AI agent thinks. Tool-using rounds
 * (search_vehicles → backend → summarise) routinely take 40-60s on a cold
 * call, and the backend allows up to 90s before giving up.
 *
 * The shared axios client in lib/api.js has a global 30s timeout, which would
 * abort a slow turn client-side while the server carried on: the tester sees a
 * network error, and the reply they never saw turns up in the transcript on the
 * next poll. Hence the per-request override, comfortably above the server's own
 * budget so the SERVER decides when a turn has failed.
 */
const TURN_TIMEOUT_MS = 180_000;

/**
 * Settings → Agent test console. Chat as a customer against the live AI agent.
 */
export default {
  /** Read-only: is this number already a customer? Creates nothing. */
  lookup: (phoneE164) =>
    api.post('/v1/simulator/lookup', { phoneE164 }).then(unwrap),

  /**
   * @param {string}  phoneE164
   * @param {object}  [opts]
   * @param {string}  [opts.channel='WHATSAPP']
   * @param {boolean} [opts.acknowledgeRealCustomer] required when the number
   *        belongs to a real customer — the backend refuses without it
   */
  startSession: (phoneE164, opts = {}) =>
    api.post('/v1/simulator/sessions', {
      phoneE164,
      channel: opts.channel || 'WHATSAPP',
      acknowledgeRealCustomer: !!opts.acknowledgeRealCustomer,
    }).then(unwrap),

  listSessions: () => api.get('/v1/simulator/sessions').then(unwrap),

  sendMessage: (sessionId, text) =>
    api.post(`/v1/simulator/sessions/${sessionId}/messages`, { text },
      { timeout: TURN_TIMEOUT_MS }).then(unwrap),

  /**
   * Send a recorded voice clip (a Blob from MediaRecorder). The backend
   * transcribes it with the tenant's configured STT provider, then runs the
   * exact same turn a typed message would. Returns { transcript, error, turn }.
   */
  sendAudio: (sessionId, blob) => {
    const form = new FormData();
    form.append('audio', blob, 'clip.webm');
    // Do NOT set Content-Type here — the browser must add the multipart boundary.
    return api.post(`/v1/simulator/sessions/${sessionId}/audio`, form,
      { timeout: TURN_TIMEOUT_MS }).then(unwrap);
  },

  transcript: (sessionId) =>
    api.get(`/v1/simulator/sessions/${sessionId}/transcript`).then(unwrap),

  /** Clear the agent's memory but keep the lead, to replay from turn one. */
  reset: (sessionId) =>
    api.post(`/v1/simulator/sessions/${sessionId}/reset`).then(unwrap),

  close: (sessionId) =>
    api.delete(`/v1/simulator/sessions/${sessionId}`).then(unwrap),

  /** Delete every simulated row in this dealership. Principal/admin only. */
  purge: (olderThanDays) =>
    api.post('/v1/simulator/purge', { olderThanDays: olderThanDays ?? null })
      .then(unwrap),
};
